import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Award, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoyaltyWidget = () => {
  const { t, i18n } = useTranslation();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/loyalty/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setError(e?.response?.status || 'network');
        // Graceful fallback: empty balance
        setData({ balance: 0, balance_eur: 0, earn_rate: 1, redeem_rate: 0.05, transactions: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, token]);

  if (authLoading) return null;
  if (!isAuthenticated) return null;
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-orange-500 to-amber-500 text-white rounded-xl shadow-lg p-6 mb-6 animate-pulse" data-testid="loyalty-widget-loading">
        <div className="h-7 w-40 bg-white/20 rounded mb-4"></div>
        <div className="h-20 bg-white/10 rounded"></div>
      </div>
    );
  }
  if (!data) return null;

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(i18n.language || 'sk');
    } catch {
      return iso;
    }
  };

  const reasonLabel = (reason) => {
    const map = {
      order_earn: t('loyalty.reason_order_earn'),
      order_redeem: t('loyalty.reason_order_redeem'),
      manual: t('loyalty.reason_manual'),
    };
    return map[reason] || reason;
  };

  return (
    <div className="bg-gradient-to-br from-orange-500 to-amber-500 text-white rounded-xl shadow-lg p-6 mb-6" data-testid="loyalty-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Award className="h-7 w-7" />
          <h2 className="text-xl font-bold">{t('loyalty.title')}</h2>
        </div>
        <TrendingUp className="h-5 w-5 opacity-70" />
      </div>

      <div className="bg-white/10 rounded-lg p-4 mb-3 backdrop-blur">
        <div className="text-sm opacity-90 mb-1">{t('loyalty.balance_label')}</div>
        <div className="text-4xl font-bold" data-testid="loyalty-balance">{data.balance}</div>
        <div className="text-sm opacity-90 mt-1">≈ €{data.balance_eur.toFixed(2)}</div>
      </div>

      <div className="text-sm opacity-90 mb-4">
        {t('loyalty.earn_info', { rate: data.earn_rate })}
      </div>

      {error && (
        <div className="text-xs opacity-70 mb-3">
          {t('loyalty.error_loading') || 'Could not load latest balance'}
        </div>
      )}

      {data.transactions.length > 0 && (
        <details className="bg-white/10 rounded-lg p-3 backdrop-blur">
          <summary className="cursor-pointer font-medium text-sm">{t('loyalty.history')} ({data.transactions.length})</summary>
          <ul className="mt-3 space-y-1 text-sm max-h-48 overflow-y-auto">
            {data.transactions.map((tx) => (
              <li key={tx.id} className="flex justify-between border-b border-white/20 pb-1">
                <span>
                  {formatDate(tx.created_at)} — <span className="opacity-80">{reasonLabel(tx.reason)}</span>
                </span>
                <span className={`font-semibold ${tx.delta >= 0 ? 'text-green-200' : 'text-red-200'}`}>
                  {tx.delta >= 0 ? '+' : ''}{tx.delta}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default LoyaltyWidget;
